import React, { useState, useEffect, useRef } from 'react';

import styles from './AddMultipleAudios.module.css';
import useAdminProfileStore from '../../../store/admin/adminProfileStore';
import { useCategories } from '../../../hooks/categories/useCategories';
import { useSeriesByType } from '../../../hooks/useSeriesData';
import { useMultipleContent } from '../../../hooks/admin/useAdminMultipleContents';
import { useAdminProfile } from '../../../hooks/admin/useAdminProfile';

const AddMultipleAudios = () => {
    const { profile:dataProfile } = useAdminProfile();
  const profile = useAdminProfileStore((state) => state.profile);
  const { categories = [] } = useCategories('Audio');
  const { series = [] } = useSeriesByType('Audio');
  const { 
    uploadBatch, 
    isUploading, 
    uploadError,
    content: uploadedAudios,
    refetch: refetchAudios
  } = useMultipleContent('audio');

  // Create refs for file inputs
  const mobileImageInputRef = useRef(null);
  const computerImageInputRef = useRef(null);
  const audioFilesInputRef = useRef(null);
  const [formData, setFormData] = useState({
    genre_id: '',
    genreName: '',  // Added genreName field
    language: '',
    is_public: false,
    have_series: false,
    series_id: '',
    author_id: dataProfile?.id || '',  // Set author_id from profile
    format_type: 'audio',
  });

  const [audioFiles, setAudioFiles] = useState([]);
  const [mobileImage, setMobileImage] = useState(null);
  const [computerImage, setComputerImage] = useState(null);
  const [audioMetadata, setAudioMetadata] = useState([]);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isCalculatingDuration, setIsCalculatingDuration] = useState(false);
  const [mobileImagePreview, setMobileImagePreview] = useState(null);
  const [computerImagePreview, setComputerImagePreview] = useState(null);
  const [seriesImage, setSeriesImage] = useState(null);
  
  useEffect(() => {
    if (profile?.id) {
      setFormData(prev => ({ ...prev, author_id: profile.id }));
    }
  }, [profile]);

  useEffect(() => {
    if (uploadError) {
      setMessage({ text: uploadError, type: 'error' });
    }
  }, [uploadError]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Update form data with the changed field
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  
    // Reset series_id if have_series is set to false
    if (name === 'have_series' && !checked) {
      setFormData(prev => ({ ...prev, series_id: '' }));
    }
    
    // If genre_id changed, also update genre_name
    if (name === 'genre_id' && value) {
      const selectedCategory = categories.find(category => category.id === value);
      if (selectedCategory) {
        setFormData(prev => ({ ...prev, genre_name: selectedCategory.name }));
      }
    }
  
    // If series_id changed, update the series image
    if (name === 'series_id' && value) {
      const selectedSeries = series.find(item => item.id === value);
      setSeriesImage(selectedSeries?.coverImageUrl || null);
    }
  };

  useEffect(() => {
    if (formData.series_id) {
      const selectedSeries = series.find(item => item.id === formData.series_id);
      console.log(selectedSeries,"selected");
      
      if (selectedSeries) {
        setSeriesImage(selectedSeries?.coverImageUrl || null);
      }
    } else {
      setSeriesImage(null);
    }
  }, [formData.series_id, series]);
  

  // Calculate audio duration using HTML5 Audio API
  const calculateAudioDuration = (file) => {
    return new Promise((resolve) => {
      const audioElement = new Audio();
      const objectUrl = URL.createObjectURL(file);
      
      audioElement.addEventListener('loadedmetadata', () => {
        // Duration in seconds
        const durationInSeconds = Math.round(audioElement.duration);
        URL.revokeObjectURL(objectUrl);
        resolve(durationInSeconds);
      });
      
      audioElement.addEventListener('error', () => {
        console.error('Error loading audio file for duration calculation');
        URL.revokeObjectURL(objectUrl);
        resolve(0); // Default to 0 if there's an error
      });
      
      audioElement.src = objectUrl;
    });
  };

  const handleAudioFilesChange = async (e) => {
    const files = Array.from(e.target.files);
    setAudioFiles(files);
    
    if (files.length > 0) {
      setIsCalculatingDuration(true);
      
      try {
        const newMetadata = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          // Extract file name without extension to use as title
          const fileName = file.name.split('.').slice(0, -1).join('.');
          
          // Calculate duration for this audio file
          const duration = await calculateAudioDuration(file);
          
          newMetadata.push({
            title: fileName || `Audio ${i + 1}`,
            description: `Description for ${fileName || `Audio ${i + 1}`}`,
            duration: duration,
            chapter_number: i + 1
          });
        }
        
        setAudioMetadata(newMetadata);
      } catch (error) {
        console.error('Error processing audio files:', error);
        setMessage({
          text: 'Error processing audio files. Please try again.',
          type: 'error'
        });
      } finally {
        setIsCalculatingDuration(false);
      }
    } else {
      setAudioMetadata([]);
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
  
      if (type === 'mobile') {
        setMobileImage(file);
        setMobileImagePreview(previewUrl);
      } else {
        setComputerImage(file);
        setComputerImagePreview(previewUrl);
      }
    }
  };
  const removeImage = (type) => {
    if (type === 'mobile') {
      setMobileImage(null);
      setMobileImagePreview(null);
      if (mobileImageInputRef.current) {
        mobileImageInputRef.current.value = '';
      }
    } else {
      setComputerImage(null);
      setComputerImagePreview(null);
      if (computerImageInputRef.current) {
        computerImageInputRef.current.value = '';
      }
    }
  };    

  const handleMetadataChange = (index, field, value) => {
    const updatedMetadata = [...audioMetadata];
    updatedMetadata[index] = { ...updatedMetadata[index], [field]: value };
    setAudioMetadata(updatedMetadata);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    try {
      if (audioFiles.length === 0) {
        throw new Error('Please select audio files to upload');
      }

      if (formData.have_series && !formData.series_id) {
        throw new Error('Please select a series when adding chapter audios');
      }

      // Validate that all required metadata fields are filled
      const missingFields = audioMetadata.findIndex(meta => !meta.title.trim());
      if (missingFields !== -1) {
        throw new Error(`Please provide a title for all audio files (File #${missingFields + 1} is missing)`);
      }

      // Create form data for submission
      const submitFormData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitFormData.append(key, formData[key]);
      });

      // Add audio files
      audioFiles.forEach(file => {
        submitFormData.append('audioFiles', file);
      });

      // Add images if selected
      if (mobileImage) {
        submitFormData.append('mobileImage', mobileImage);
      }
      
      if (computerImage) {
        submitFormData.append('computerImage', computerImage);
      }

      // Add audio metadata
      submitFormData.append('audioData', JSON.stringify(audioMetadata));

      // Upload using the hook's mutation function
      uploadBatch(submitFormData, {
        onSuccess: (data) => {
          setMessage({ 
            text: `Success! ${data.audios.length} audio files have been uploaded.`, 
            type: 'success' 
          });
          
          // Reset form
          setFormData({
            genre_id: '',
            language: '',
            is_public: false,
            have_series: false,
            series_id: '',
            author_id: profile?.id || '',
            format_type: 'audio',
          });
          setAudioFiles([]);
          setMobileImage(null);
          setComputerImage(null);
          setAudioMetadata([]);
          
          // Reset image previews as well
          setMobileImagePreview(null);
          setComputerImagePreview(null);
          setSeriesImage(null);
          
          // Reset file input elements
          if (mobileImageInputRef.current) mobileImageInputRef.current.value = '';
          if (computerImageInputRef.current) computerImageInputRef.current.value = '';
          if (audioFilesInputRef.current) audioFilesInputRef.current.value = '';
          
          // Refresh the audio list
          refetchAudios();
        }
      });
      
    } catch (error) {
      console.error('Error preparing audio upload:', error);
      setMessage({ 
        text: error.message || 'Failed to prepare audio files for upload', 
        type: 'error' 
      });
    }
  };

  // Format duration from seconds to mm:ss
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' + secs : secs}`;
  };

  // REmove audio file and its metadata
  const handleRemoveAudio = (indexToRemove) => {
    setAudioFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setAudioMetadata(prev => prev.filter((_, index) => index !== indexToRemove));
  };
  

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Add Multiple Audio Files</h1>
      
      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label htmlFor="genre_id">Genre</label>
          <select 
            id="genre_id" 
            name="genre_id" 
            value={formData.genre_id} 
            onChange={handleChange}
            required
            className={styles.select}
          >
            <option value="">Select Genre</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="language">Language</label>
          <select 
            id="language" 
            name="language" 
            value={formData.language} 
            onChange={handleChange}
            required
            className={styles.select}
          >
            <option value="">Select Language</option>
            <option value="english">English</option>
            <option value="spanish">Spanish</option>
            <option value="french">French</option>
            <option value="german">German</option>
            <option value="chinese">Chinese</option>
            <option value="japanese">Japanese</option>
            <option value="arabic">Arabic</option>
            <option value="hindi">Hindi</option>
            <option value="garhwali">Garhwali</option>
            <option value="kumaoni">Kumaoni</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              name="is_public" 
              checked={formData.is_public} 
              onChange={handleChange}
              className={styles.checkbox}
            />
            Make Public
          </label>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.checkboxLabel}>
            <input 
              type="checkbox" 
              name="have_series" 
              checked={formData.have_series} 
              onChange={handleChange}
              className={styles.checkbox}
            />
            Part of a Series
          </label>
        </div>

        {formData.have_series && (
          <div className={styles.formGroup}>
            <label htmlFor="series_id">Series</label>
            <select 
              id="series_id" 
              name="series_id" 
              value={formData.series_id} 
              onChange={handleChange}
              required={formData.have_series}
              className={styles.select}
            >
              <option value="">Select Series</option>
              {series.map(item => (
                <option key={item.id} value={item.id}>
                  {item.title}
                </option>
              ))}
            </select>
            {seriesImage && (
              <div className={styles.seriesImagePreview}>
                <label>Series Image Preview:</label>
                <img src={seriesImage} alt="Series cover" className={styles.previewImg} />
              </div>
            )}
          </div>
        )}
        
        <div className={styles.formGroup}>
          <label>Cover Images</label>
          <div className={styles.imageUploadContainer}>
            <div className={styles.imageUpload}>
              <label htmlFor="mobileImage">Mobile Cover Image</label>
              <input 
                type="file" 
                id="mobileImage" 
                name="mobileImage" 
                accept="image/*" 
                onChange={(e) => handleImageChange(e, 'mobile')}
                className={styles.fileInput}
                ref={mobileImageInputRef}
              />
              {mobileImagePreview && (
                <div className={styles.imagePreview}>
                  <img src={mobileImagePreview} alt="Mobile preview" className={styles.previewImg} />
                  <button 
                    type="button" 
                    className={styles.removeBtn}
                    onClick={() => removeImage('mobile')}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>

            <div className={styles.imageUpload}>
              <label htmlFor="computerImage">Desktop Cover Image</label>
              <input 
                type="file" 
                id="computerImage" 
                name="computerImage" 
                accept="image/*" 
                onChange={(e) => handleImageChange(e, 'computer')}
                className={styles.fileInput}
                ref={computerImageInputRef}
              />
              {computerImagePreview && (
                <div className={styles.imagePreview}>
                  <img src={computerImagePreview} alt="Desktop preview" className={styles.previewImg} />
                  <button 
                    type="button" 
                    className={styles.removeBtn}
                    onClick={() => removeImage('computer')}
                  >
                    &times;
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

  
        <div className={styles.formGroup}>
          <label htmlFor="audioFiles">Audio Files</label>
          <input 
            type="file" 
            id="audioFiles" 
            name="audioFiles" 
            accept="audio/*" 
            multiple 
            onChange={handleAudioFilesChange}
            required
            className={styles.fileInput}
            ref={audioFilesInputRef}
          />
          {audioFiles.length > 0 && (
            <div className={styles.filesPreview}>
              <span>{audioFiles.length} files selected</span>
              {isCalculatingDuration && (
                <div className={styles.loadingIndicator}>
                  Calculating audio durations...
                </div>
              )}
            </div>
          )}
        </div>
        
        {audioFiles.length > 0 && !isCalculatingDuration && (
          <div className={styles.metadataSection}>
            <h3>Audio File Details</h3>
            
            {audioMetadata.map((meta, index) => (
              <div key={index} className={styles.metadataItem}>
                <div className={styles.metadataHeader}>
                  <h4>
                    File {index + 1}: {audioFiles[index]?.name}
                  </h4>
                  <button
                    type="button"
                    className={styles.removeAudioBtn}
                    onClick={() => handleRemoveAudio(index)}
                    title="Remove this audio"
                  >
                    &times;
                  </button>
                </div>
                <span className={styles.durationBadge}>
                  Duration: {formatDuration(meta.duration)}
                </span>
                
                <div className={styles.formGroup}>
                  <label htmlFor={`title-${index}`}>Title</label>
                  <input 
                    type="text" 
                    id={`title-${index}`} 
                    value={meta.title} 
                    onChange={(e) => handleMetadataChange(index, 'title', e.target.value)}
                    required
                    className={styles.input}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor={`description-${index}`}>Description</label>
                  <textarea 
                    id={`description-${index}`} 
                    value={meta.description} 
                    onChange={(e) => handleMetadataChange(index, 'description', e.target.value)}
                    className={styles.textarea}
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label htmlFor={`duration-${index}`}>Duration (seconds) - Auto-calculated</label>
                  <input 
                    type="number" 
                    id={`duration-${index}`} 
                    value={meta.duration} 
                    onChange={(e) => handleMetadataChange(index, 'duration', Number(e.target.value))}
                    min="0"
                    className={styles.input}
                    readOnly
                  />
                </div>
                
                {formData.have_series && (
                  <div className={styles.formGroup}>
                    <label htmlFor={`chapter-${index}`}>Chapter Number</label>
                    <input 
                      type="number" 
                      id={`chapter-${index}`} 
                      value={meta.chapter_number} 
                      onChange={(e) => handleMetadataChange(index, 'chapter_number', Number(e.target.value))}
                      min="1"
                      required={formData.have_series}
                      className={styles.input}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.formAction}>
          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isUploading || isCalculatingDuration}
          >
            {isUploading ? 'Uploading...' : 'Upload Audio Files'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMultipleAudios;